import { isEmpty, isNil } from 'lodash';
import { useMemo } from 'react';
import { FieldRecord } from '@shared/scenes/entity/types';
import { i18n } from '@shared/util/i18n';

export type CardEntityError = { description: string | null; componentId?: string };

interface FormErrorsProps {
  apiErrors?: CardEntityError[];
  fields?: FieldRecord;
  components: MBAPI.WorkflowTemplate_components[];
  includeOnSubmitErrors?: boolean;
  includeLocalErrors?: boolean;
}

interface FormErrors {
  errors: CardEntityError[];
  isValid: boolean;
}

export function useFormErrors({
  apiErrors,
  fields,
  components,
  includeOnSubmitErrors = true,
  includeLocalErrors = true,
}: FormErrorsProps): FormErrors {
  const requiredErrors = useRequiredErrors({ fields, components });
  const localValidationErrors = useLocalValidationErrors({ fields });
  const serverErrors = apiErrors?.map(genericizeApiError) ?? [];

  let errors = [
    ...(includeOnSubmitErrors ? requiredErrors : []),
    ...(includeLocalErrors ? localValidationErrors : []),
    ...serverErrors,
  ];

  const hasComponentLevelErrors = errors.some(error => !isNil(error.componentId));
  const hasFormLevelErrors = errors.some(error => isNil(error.componentId));

  if (hasComponentLevelErrors && !hasFormLevelErrors && includeOnSubmitErrors) {
    errors = [...errors, { description: i18n('card.form.errorBanner'), componentId: undefined }];
  }

  return {
    errors,
    isValid: isEmpty(requiredErrors) && isEmpty(localValidationErrors),
  };
}

export function useRequiredErrors({
  fields,
  components,
}: {
  fields?: FieldRecord;
  components: MBAPI.WorkflowTemplate_components[];
}): CardEntityError[] {
  return useMemo(
    () =>
      components
        .filter(component => 'required' in component && component.required)
        .filter(component => isNil(fields?.[component.id]) || fields?.[component.id]?.isEmpty)
        .map(component => ({
          description: i18n('card.form.errors.required'),
          componentId: component.id,
        })),
    [components, fields],
  );
}

function useLocalValidationErrors({ fields }: { fields?: FieldRecord }): CardEntityError[] {
  return useMemo(
    () =>
      Object.keys(fields ?? {})
        .filter(componentId => !isNil(fields?.[componentId]?.validate()))
        .map(componentId => ({
          description: fields?.[componentId]?.validate() ?? '',
          componentId,
        })),
    [fields],
  );
}

// TODO server errors not currently usable in UI, so
// using generic error message for now.
// Eventually shape should include applicable componentId.
// when returned error (see useSaveNewCardEntity) is available,
// can use that directly and remove this mapping.
function genericizeApiError(): CardEntityError {
  return {
    description: i18n('card.form.errors.generic'),
    // componentId,
  };
}
